import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, Pagination, Typography } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { sxDarkGreyBody, sxDivider, sxPagination, sxPaginationContrast } from "../../../../assets/styles/CommonSxProps";
import VoltarButton from "../../../../components/Accessibility/VoltarButton";
import FilterSelect from "../../../../components/FilterSelect";
import LoadingApp from "../../../../components/LoadingApp";
import { DoctorGC } from "../../../../interfaces/entities/SaudeBemEstar/GestaoDeCronicos/DoctorGC";
import { FiltroAgendamentoGC } from "../../../../interfaces/entities/SaudeBemEstar/GestaoDeCronicos/FiltroAgendamentoGC";
import { StepProps } from "../../../../interfaces/entities/SaudeBemEstar/GestaoDeCronicos/StepProps";
import { TipoAgendamento } from "../../../../interfaces/entities/SaudeBemEstar/GestaoDeCronicos/TipoAgendamento";
import { getFiltersAgendamentoCronicos, getPrestadoresAgendamentoGestao } from "../../../../services/SaudeBemEstar/GestaoDeCronicosService";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import { scrollTo } from "../../../../utils/ScrollUtil";
import DoctorCard from "./DoctorCard";

const StepProfissional: React.FC<StepProps> = ({ backStep, onProceedProfissional, openModalError }) => {

  const accessibility = useAccessibility();
  const [loading, setLoading] = useState<boolean>(false);
  const [filtrosAgendamento, setFiltrosAgendamento] = useState<FiltroAgendamentoGC[]>([]);
  const [filtroSelecionado, setFiltroSelecionado] = useState<FiltroAgendamentoGC>();
  const [availableDoctors, setAvailableDoctors] = useState<DoctorGC[]>([]);
  const [idSelectedFilterDoctor, setIdSelectedFilterDoctor] = useState<string>();
  const [tipoAgendamentoSelecionado, setTipoAgendamentoSelecionado] = useState<TipoAgendamento>();
  const [page, setPage] = useState(1);

  const filteredDoctors = availableDoctors.filter((doctor) => {
    return idSelectedFilterDoctor === 'todos' || !idSelectedFilterDoctor || doctor.idPrestador === idSelectedFilterDoctor;
  });

  const paginatedAvailableDoctors = filteredDoctors.slice(
    (page - 1) * 5,
    page * 5
  );

  const fetchAvailableDoctors = async (idEspec: number, tipoAgendamento: string) => {
    setLoading(true);
    const response = await getPrestadoresAgendamentoGestao(idEspec, tipoAgendamento);
    if (response?.status === 200) {
      setAvailableDoctors(response.data);
    } else {
      console.error(response);
      openModalError('Erro', 'Ocorreu um erro ao consultar as opções de agendamento.');
    }
    setLoading(false);
  }

  const fetchFilters = async () => {
    setLoading(true);
    const response = await getFiltersAgendamentoCronicos();
    if (response?.status === 200) {
      setFiltrosAgendamento(response.data);
    } else {
      console.error(response);
      openModalError('Erro', 'Ocorreu um erro ao consultar os filtros de agendamento.');
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchFilters();
  }, [])

  const handleSelectFilter = (value: string) => {
    setFiltroSelecionado(filtrosAgendamento.find((item) => item.idEspec.toString() === value));
    fetchAvailableDoctors(Number(value), "");
  }

  const handleSelectType = (value: string) => {
    setTipoAgendamentoSelecionado(filtroSelecionado?.lstTipoAgendamento.find((item) => item.value === value));
    fetchAvailableDoctors(filtroSelecionado!.idEspec, value);
    setIdSelectedFilterDoctor('todos');
  }

  const handleSelectDoctorToFilter = (value: string) => {
    setIdSelectedFilterDoctor(value === "todos" ? value : availableDoctors.find((item) => item.idPrestador === value)?.idPrestador);
  }

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo('filter-row-agendamento');
  };

  return (
    <>
      <LoadingApp showLoading={loading} text={'Carregando...'} />
      <div className='feature-container'>
        <Box width={'100%'}>
          <h1 className='filter-title' style={accessibility.textStyleAccessibility}>
            <FontAwesomeIcon icon={faFilter} />&nbsp;Filtrar resultados
          </h1>
          <div className={"filter-row-agendamento"}>
            <div className={"filter-option-agendamento"}>
              <FilterSelect title='Tipo de profissional'
                options={filtrosAgendamento.map((item) => { return { id: item.idEspec.toString(), descricao: item.especialidade } })} disabled={false}
                handleChange={handleSelectFilter} erase={false} placeholder="Selecione o tipo"
              />
            </div>
            <div className={"filter-option-agendamento"}>
              <FilterSelect title='Tipo de agendamento'
                options={filtroSelecionado?.lstTipoAgendamento.map((item) => { return { id: item.value, descricao: item.titulo } }) || []}
                disabled={!filtroSelecionado}
                handleChange={handleSelectType} erase={true} placeholder="Selecione o agendamento"
              />
            </div>
            <div className="filter-option-agendamento">
              <FilterSelect title='Profissional'
                options={
                  [{ id: 'todos', descricao: 'Todos(as)' }, ...availableDoctors.map((item) => { return { id: item.idPrestador, descricao: item.nome } })]
                }
                disabled={!filtroSelecionado || !tipoAgendamentoSelecionado || loading}
                value={idSelectedFilterDoctor}
                handleChange={handleSelectDoctorToFilter} erase={true} placeholder="Selecione o(a) profissional"
              />
            </div>
          </div>
          <Divider sx={sxDivider} />
        </Box>
        <>
          {(availableDoctors.length === 0 && !!filtroSelecionado && !!tipoAgendamentoSelecionado && !loading) ?
            <Typography sx={sxDarkGreyBody}>Nenhum(a) profissional encontrado(a)</Typography> :
            paginatedAvailableDoctors.map((item) => {
              return <DoctorCard
                key={item.idPrestador}
                doctor={item}
                handleButtonClick={() => onProceedProfissional!(item, tipoAgendamentoSelecionado?.value!, filtroSelecionado?.idPlanoCuidPac!)}
              />
            })
          }
        </>
        {filteredDoctors.length > 5 && !loading &&
          <div className='pagination-holder'>
            <Pagination
              count={Math.ceil(filteredDoctors.length / 5)}
              sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
              color='primary'
              page={page}
              onChange={handlePageChange}
            />
          </div>
        }
        <VoltarButton onClick={backStep} />
      </div>
    </>
  )
}

export default StepProfissional;